import {
  type PropsWithChildren,
  Suspense,
  type SuspenseProps,
  useEffect,
  useState
} from "react"
import { Outlet, useNavigate } from "react-router-dom"

import { RouteStateProvider } from "../app/routing/RouteStateContext"
import { useHasCustomTypes } from "../documents/DocumentListBody/useHasCustomTypes"

interface RedirectHandlerProps extends PropsWithChildren {
  to: "working" | "onboarding"
}

function RedirectHandler(props: RedirectHandlerProps) {
  const { children, to } = props

  const navigate = useNavigate()
  const hasCustomTypes = useHasCustomTypes()
  const [shouldRenderChildren, setShouldRenderChildren] = useState(false)

  useEffect(() => {
    const redirectConditions: Record<typeof to, boolean> = {
      onboarding: !hasCustomTypes,
      working: hasCustomTypes
    }

    if (redirectConditions[to]) {
      navigate(`/${to}`, { replace: true })
    } else {
      // Render children if redirect is not needed
      setShouldRenderChildren(true)
    }
  }, [hasCustomTypes, navigate, to])

  return <>{shouldRenderChildren ? children : null}</>
}

interface OnboardingRedirectWrapperProps extends RedirectHandlerProps {
  suspenseFallback?: SuspenseProps["fallback"]
}

export function OnboardingRedirectGuard(props: OnboardingRedirectWrapperProps) {
  const { suspenseFallback, to } = props
  return (
    <Suspense fallback={suspenseFallback}>
      <RedirectHandler to={to}>
        <RouteStateProvider>
          <Outlet />
        </RouteStateProvider>
      </RedirectHandler>
    </Suspense>
  )
}
